import React from 'react';
import { Container, Typography, Grid, Card, CardContent, CardActions, Button, Box } from '@mui/material';
import { GitHub as GitHubIcon, Language as LanguageIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import projectsData from '../data/projects.json';

const Projects = () => {
  const { projects } = projectsData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };


  return (
    <Box 
      component="section"
      sx={{ 
        py: { xs: 6, md: 12 },
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        background: 'transparent',
        borderRadius: 2,
        boxShadow: 'none'
      }}
    >
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div variants={itemVariants}>
            <Typography 
              variant="h3" 
              gutterBottom 
              align="center" 
              sx={{ 
                mb: 6,
                fontFamily: "'Chivo Mono', monospace",
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ffffff, rgba(255, 255, 255, 0.7))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.1))',
                opacity: 1
              }}
            >
              Featured Projects
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {projects.map((project, projectIndex) => (
              <Grid item xs={12} sm={6} key={projectIndex}>
                <motion.div 
                  variants={itemVariants}
                  whileHover={{ 
                    rotateX: 2,
                    rotateY: -2,
                    transition: { duration: 0.3 }
                  }}
                  style={{ height: '100%', perspective: '1000px' }}
                >
                  <Card
                    className="glass-card"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        transform: 'none', // Handled by glass-card class
                        boxShadow: 'none', // Handled by glass-card class
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        '& .project-tech-chip': {
                          transform: 'translateY(-2px)',
                          bgcolor: 'rgba(255, 255, 255, 0.15)'
                        }
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                      <Typography 
                        variant="h5" 
                        component="h2" 
                        gutterBottom
                        sx={{ 
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
                          mb: 2,
                          opacity: 1
                        }}
                      >
                        {project.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        gutterBottom
                        sx={{ 
                          color: 'var(--text-dim)',
                          mb: 2,
                          fontSize: '0.95rem',
                          opacity: 0.8
                        }}
                      >
                        {project.date}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        paragraph
                        sx={{ 
                          color: '#ffffff',
                          mb: 4,
                          lineHeight: 1.7,
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          opacity: 0.9
                        }}
                      >
                        {project.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>
                        {project.tech.map((technologyName, techIndex) => (
                          <Typography
                            key={techIndex}
                            variant="body2"
                            className="project-tech-chip"
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                              color: '#ffffff',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.85rem',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                bgcolor: 'rgba(255, 255, 255, 0.1)'
                              }
                            }}
                          >
                            {technologyName}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ display: 'flex', gap: 1, px: 3, pb: 3, pt: 0 }}>
                      {project.github && (
                        <Button 
                          size="small" 
                          startIcon={<GitHubIcon />}
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            color: '#ffffff',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            border: '1px solid',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: '#ffffff'
                            }
                          }}
                        >
                          Code
                        </Button>
                      )}
                      {project.url && project.url.length > 0 && (
                        <Button 
                          size="small" 
                          startIcon={<LanguageIcon />}
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            color: '#ffffff',
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            border: '1px solid',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: '#ffffff'
                            }
                          }}
                        >
                          Demo
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Projects; 